import GermanDateInput from "@/components/GermanDateInput";
import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  KeyRound,
  Plus,
  UserPlus,
  UserMinus,
  Copy,
  Eye,
  Edit3,
  Trash2,
  Download,
  History,
  Package,
  AlertTriangle,
  ExternalLink,
  X,
  Check,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// ─────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────
const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text || "");
    toast.success("In Zwischenablage kopiert");
  } catch (_) {
    toast.error("Kopieren fehlgeschlagen");
  }
};

const formatEUR = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

const daysBadge = (daysLeft, expired) => {
  if (daysLeft == null) return null;
  if (expired)
    return (
      <span className="text-[10px] font-bold uppercase tracking-overline px-1.5 py-0.5 bg-red-600 text-white">
        Abgelaufen
      </span>
    );
  if (daysLeft <= 14)
    return (
      <span className="text-[10px] font-bold uppercase tracking-overline px-1.5 py-0.5 bg-[#FFD100] text-[#0A0A0A]">
        {daysLeft} Tage
      </span>
    );
  return (
    <span className="text-[10px] font-mono uppercase text-slate-500">{daysLeft} Tage</span>
  );
};

// ─────────────────────────────────────────────────────────
//  Software icon (Logo-Bild oder Emoji-Fallback)
// ─────────────────────────────────────────────────────────
function TemplateIcon({ template, size = "w-11 h-11 text-2xl", testId }) {
  const t = template || {};
  return (
    <div
      className={`${size} flex items-center justify-center shrink-0 overflow-hidden`}
      style={{ background: `${t.color || "#00CCC9"}18`, color: t.color || "#00CCC9" }}
      data-testid={testId || `tpl-icon-${t.template_id}`}
    >
      {t.has_logo ? (
        <img
          src={`${API}/licenses/templates/${t.template_id}/logo`}
          alt={t.name || "Logo"}
          className="w-full h-full object-contain p-1"
        />
      ) : (
        t.icon_emoji || "🔑"
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────────────────
export default function LicensesPage() {
  const [tab, setTab] = useState("slots");
  const [templates, setTemplates] = useState([]);
  const [slots, setSlots] = useState([]);
  const [history, setHistory] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSlot, setEditSlot] = useState(null); // slot object or {} for new
  const [assignSlot, setAssignSlot] = useState(null);
  const [editTemplate, setEditTemplate] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [t, s, p] = await Promise.all([
        api.get("/licenses/templates"),
        api.get("/licenses/slots"),
        api.get("/licenses/participants"),
      ]);
      setTemplates(t.data || []);
      setSlots(s.data || []);
      setParticipants(p.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  const loadHistory = async () => {
    try {
      const r = await api.get("/licenses/history");
      setHistory(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);
  useEffect(() => {
    if (tab === "history") loadHistory();
  }, [tab]);

  const stats = useMemo(() => {
    const total = slots.length;
    const assigned = slots.filter((s) => s.current_assignment).length;
    const free = total - assigned;
    const expiringSoon = slots.filter((s) => s.expiring_soon && !s.expired).length;
    const monthlyCost = slots.reduce((sum, s) => sum + (s.monthly_cost || 0), 0);
    return { total, assigned, free, expiringSoon, monthlyCost };
  }, [slots]);

  const downloadPdf = () => {
    window.open(`${API}/licenses/report.pdf`, "_blank");
  };

  return (
    <div className="max-w-[1200px] mx-auto p-6 lg:p-10" data-testid="licenses-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
          <KeyRound size={12} strokeWidth={1.5} /> Portal · Verwaltung
        </div>
        <div className="flex items-baseline justify-between flex-wrap gap-4 mt-1">
          <h1 className="font-heading text-5xl font-light tracking-tighter text-[#0A0A0A]">
            Software-<span className="text-[#00CCC9]">Lizenzen</span>
          </h1>
          <div className="flex gap-2">
            <button
              onClick={downloadPdf}
              data-testid="lic-pdf-btn"
              className="border border-slate-300 hover:bg-slate-900 hover:text-white px-4 py-2 text-xs font-medium flex items-center gap-2"
            >
              <Download size={12} strokeWidth={1.5} /> PDF-Report
            </button>
            <button
              onClick={() => setEditSlot({})}
              data-testid="lic-new-slot-btn"
              className="bg-[#00CCC9] text-[#0A0A0A] hover:brightness-105 px-4 py-2 text-xs font-semibold flex items-center gap-2"
            >
              <Plus size={14} strokeWidth={1.5} /> Neuer Slot
            </button>
          </div>
        </div>
      </motion.div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <KpiCard label="Slots gesamt" value={stats.total} icon={Package} />
        <KpiCard label="Zugewiesen" value={stats.assigned} icon={Check} tone="green" />
        <KpiCard label="Verfügbar" value={stats.free} icon={UserPlus} tone="teal" />
        <KpiCard
          label="Ablauf ≤ 14 Tage"
          value={stats.expiringSoon}
          icon={AlertTriangle}
          tone="yellow"
        />
        <KpiCard label="Monatliche Kosten" value={formatEUR(stats.monthlyCost)} icon={KeyRound} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6" data-testid="lic-tabs">
        <Tab active={tab === "slots"} onClick={() => setTab("slots")} testId="lic-tab-slots">
          Lizenz-Slots
        </Tab>
        <Tab
          active={tab === "templates"}
          onClick={() => setTab("templates")}
          testId="lic-tab-templates"
        >
          Software-Typen
        </Tab>
        <Tab active={tab === "history"} onClick={() => setTab("history")} testId="lic-tab-history">
          Historie
        </Tab>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500">Lädt…</div>
      ) : tab === "slots" ? (
        <SlotsView
          slots={slots}
          onNew={() => setEditSlot({})}
          onEdit={(s) => setEditSlot(s)}
          onAssign={(s) => setAssignSlot(s)}
          onRevoke={async (s) => {
            if (!window.confirm(`Zugang von ${s.current_assignment?.participant_name} entziehen?`)) return;
            try {
              await api.post(`/licenses/slots/${s.slot_id}/revoke`, { reason: "manual" });
              toast.success("Zugang entzogen");
              await loadAll();
            } catch (e) {
              toast.error(e.response?.data?.detail || e.message);
            }
          }}
          onArchive={async (s) => {
            if (!window.confirm(`Slot "${s.slot_label}" archivieren?`)) return;
            try {
              await api.delete(`/licenses/slots/${s.slot_id}`);
              toast.success("Slot archiviert");
              await loadAll();
            } catch (e) {
              toast.error(e.response?.data?.detail || e.message);
            }
          }}
        />
      ) : tab === "templates" ? (
        <TemplatesView
          templates={templates}
          onNew={() => setEditTemplate({})}
          onEdit={(t) => setEditTemplate(t)}
          onDelete={async (t) => {
            if (!window.confirm(`Software-Typ "${t.name}" löschen?`)) return;
            try {
              await api.delete(`/licenses/templates/${t.template_id}`);
              toast.success("Gelöscht");
              await loadAll();
            } catch (e) {
              toast.error(e.response?.data?.detail || e.message);
            }
          }}
        />
      ) : (
        <HistoryView history={history} />
      )}

      {editSlot !== null && (
        <SlotDialog
          initial={editSlot}
          templates={templates}
          onClose={() => setEditSlot(null)}
          onSaved={async () => {
            setEditSlot(null);
            await loadAll();
          }}
        />
      )}
      {assignSlot && (
        <AssignDialog
          slot={assignSlot}
          participants={participants}
          onClose={() => setAssignSlot(null)}
          onSaved={async () => {
            setAssignSlot(null);
            await loadAll();
          }}
        />
      )}
      {editTemplate !== null && (
        <TemplateDialog
          initial={editTemplate}
          onClose={() => setEditTemplate(null)}
          onSaved={async () => {
            setEditTemplate(null);
            await loadAll();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  Slots
// ─────────────────────────────────────────────────────────
const SLOT_FILTERS = [
  ["all", "Alle"],
  ["active", "Aktiv"],
  ["inactive", "Inaktiv"],
  ["expired", "Abgelaufen"],
];
const slotState = (s) => (s.expired ? "expired" : s.current_assignment ? "active" : "inactive");

function SlotsView({ slots, onNew, onEdit, onAssign, onRevoke, onArchive }) {
  const [status, setStatus] = useState("all");
  const counts = useMemo(() => slots.reduce((m, s) => ({ ...m, [slotState(s)]: (m[slotState(s)] || 0) + 1 }), { all: slots.length }), [slots]);
  const shown = status === "all" ? slots : slots.filter((s) => slotState(s) === status);
  if (!slots.length) {
    return (
      <div className="border border-dashed border-slate-300 p-10 text-center">
        <KeyRound size={32} strokeWidth={1.5} className="mx-auto text-slate-400 mb-3" />
        <div className="font-medium text-slate-700">Noch keine Lizenz-Slots angelegt</div>
        <p className="text-xs text-slate-500 mt-2 mb-4">
          Lege den ersten Zugang für Canva, Figma, Kling AI etc. an.
        </p>
        <button
          onClick={onNew}
          data-testid="lic-empty-new-btn"
          className="bg-[#00CCC9] text-[#0A0A0A] px-4 py-2 text-xs font-semibold hover:brightness-105"
        >
          Ersten Slot anlegen
        </button>
      </div>
    );
  }
  return (
    <>
    <div className="flex gap-1.5 mb-4 flex-wrap" data-testid="lic-status-filter">
      {SLOT_FILTERS.map(([k, l]) => (
        <button key={k} onClick={() => setStatus(k)} data-testid={`lic-filter-${k}`}
          className={`px-3 py-1.5 text-xs font-medium border transition-colors ${status === k ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
          {l} <span className="opacity-60 ml-1">{counts[k] || 0}</span>
        </button>
      ))}
    </div>
    {shown.length === 0 && <div className="border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="lic-filter-empty">Keine Lizenzen in diesem Status.</div>}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lic-slots-grid">
      {shown.map((s) => (
        <SlotCard
          key={s.slot_id}
          slot={s}
          onEdit={() => onEdit(s)}
          onAssign={() => onAssign(s)}
          onRevoke={() => onRevoke(s)}
          onArchive={() => onArchive(s)}
        />
      ))}
    </div>
    </>
  );
}

function SlotCard({ slot, onEdit, onAssign, onRevoke, onArchive }) {
  const [revealed, setRevealed] = useState(null);
  const t = slot.template || {};
  const asg = slot.current_assignment;

  const reveal = async () => {
    try {
      const r = await api.post(`/licenses/slots/${slot.slot_id}/reveal`);
      setRevealed(r.data);
      // auto-hide after 30s
      setTimeout(() => setRevealed(null), 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };
  const doCopyPw = async () => {
    if (revealed?.password) return copy(revealed.password);
    try {
      const r = await api.post(`/licenses/slots/${slot.slot_id}/reveal`);
      await copy(r.data.password);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div
      data-testid={`slot-card-${slot.slot_id}`}
      className="border border-slate-200 bg-white flex flex-col"
      style={{ borderTop: `3px solid ${t.color || "#00CCC9"}` }}
    >
      <div className="p-4 flex items-start gap-3">
        <TemplateIcon template={t} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">
            {t.name || "Software"} · Slot {slot.slot_label}
          </div>
          <div className="font-mono text-sm text-slate-900 truncate mt-0.5">{slot.login_email}</div>
          <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            {slot.expires_on && (
              <>
                <span>Ablauf {slot.expires_on && slot.expires_on.length >= 10 ? `${slot.expires_on.slice(8, 10)}.${slot.expires_on.slice(5, 7)}.${slot.expires_on.slice(0, 4)}` : slot.expires_on}</span>
                {daysBadge(slot.days_left, slot.expired)}
              </>
            )}
            {slot.monthly_cost > 0 && <span>· {formatEUR(slot.monthly_cost)}/Mon.</span>}
          </div>
        </div>
      </div>

      {/* Assignment status */}
      <div className="px-4 pb-3">
        {asg ? (
          <div className="border-l-2 border-[#00CCC9] bg-[#00CCC9]/5 px-3 py-2">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">
              Zugewiesen an
            </div>
            <div className="text-sm font-medium text-slate-900 truncate">
              {asg.participant_name}
            </div>
            {asg.class && (
              <div className="text-[10px] text-slate-500 font-mono">
                {asg.class.measure_number} · {asg.class.name}
              </div>
            )}
          </div>
        ) : (
          <div className="border-l-2 border-slate-300 bg-slate-50 px-3 py-2">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">
              Status
            </div>
            <div className="text-sm font-medium text-slate-700">— Verfügbar —</div>
          </div>
        )}
      </div>

      {/* Password preview (temporary reveal) */}
      {revealed && (
        <div className="mx-4 mb-3 p-3 border border-[#FFD100] bg-[#FFFBE6]">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">
            Passwort · sichtbar 30 s
          </div>
          <div className="font-mono text-sm break-all">{revealed.password}</div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-auto p-3 border-t border-slate-100 flex flex-wrap gap-2">
        <button
          onClick={doCopyPw}
          className="flex-1 text-xs bg-slate-900 text-white px-3 py-2 hover:bg-[#00CCC9] hover:text-[#0A0A0A] transition-colors flex items-center justify-center gap-1 min-w-[110px]"
          data-testid={`slot-copy-${slot.slot_id}`}
          title="Passwort in Zwischenablage kopieren"
        >
          <Copy size={12} strokeWidth={1.5} /> PW kopieren
        </button>
        <button
          onClick={reveal}
          className="text-xs border border-slate-300 hover:bg-slate-100 px-3 py-2 flex items-center justify-center gap-1"
          data-testid={`slot-reveal-${slot.slot_id}`}
          title="Passwort kurz anzeigen"
        >
          <Eye size={12} strokeWidth={1.5} />
        </button>
        {t.login_url && (
          <a
            href={t.login_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs border border-slate-300 hover:bg-slate-100 px-3 py-2 flex items-center justify-center gap-1"
            title="Login-Seite öffnen"
          >
            <ExternalLink size={12} strokeWidth={1.5} />
          </a>
        )}
        {asg ? (
          <button
            onClick={onRevoke}
            className="text-xs border border-red-300 text-red-700 hover:bg-red-600 hover:text-white px-3 py-2 flex items-center justify-center gap-1"
            data-testid={`slot-revoke-${slot.slot_id}`}
          >
            <UserMinus size={12} strokeWidth={1.5} /> Entziehen
          </button>
        ) : (
          <button
            onClick={onAssign}
            className="text-xs border border-[#00CCC9] text-[#009F9C] hover:bg-[#00CCC9] hover:text-[#0A0A0A] px-3 py-2 flex items-center justify-center gap-1"
            data-testid={`slot-assign-${slot.slot_id}`}
          >
            <UserPlus size={12} strokeWidth={1.5} /> Zuweisen
          </button>
        )}
        <button
          onClick={onEdit}
          className="text-xs border border-slate-300 hover:bg-slate-100 px-3 py-2"
          data-testid={`slot-edit-${slot.slot_id}`}
          title="Bearbeiten"
        >
          <Edit3 size={12} strokeWidth={1.5} />
        </button>
        <button
          onClick={onArchive}
          className="text-xs border border-slate-300 text-slate-500 hover:bg-slate-100 px-3 py-2"
          data-testid={`slot-archive-${slot.slot_id}`}
          title="Archivieren"
        >
          <Trash2 size={12} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  Templates
// ─────────────────────────────────────────────────────────
function TemplatesView({ templates, onNew, onEdit, onDelete }) {
  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={onNew}
          data-testid="lic-new-template-btn"
          className="bg-[#0A0A0A] text-white px-4 py-2 text-xs font-medium hover:bg-[#00CCC9] hover:text-[#0A0A0A] flex items-center gap-2"
        >
          <Plus size={14} strokeWidth={1.5} /> Neuer Software-Typ
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="lic-templates-grid">
        {templates.map((t) => (
          <div
            key={t.template_id}
            className="border border-slate-200 bg-white p-4 flex items-center gap-3"
            style={{ borderLeft: `3px solid ${t.color || "#00CCC9"}` }}
            data-testid={`tpl-card-${t.template_id}`}
          >
            <TemplateIcon template={t} testId={`tpl-card-icon-${t.template_id}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{t.name}</div>
              <div className="text-[10px] text-slate-500 truncate">
                {t.login_url || "—"} {t.monthly_cost > 0 ? `· ${formatEUR(t.monthly_cost)}/Mon.` : ""}
              </div>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => onEdit(t)}
                className="p-2 border border-slate-300 hover:bg-slate-100"
                title="Bearbeiten"
                data-testid={`tpl-edit-${t.template_id}`}
              >
                <Edit3 size={12} strokeWidth={1.5} />
              </button>
              <button
                onClick={() => onDelete(t)}
                className="p-2 border border-slate-300 text-red-600 hover:bg-red-600 hover:text-white"
                title="Löschen"
                data-testid={`tpl-delete-${t.template_id}`}
              >
                <Trash2 size={12} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  History
// ─────────────────────────────────────────────────────────
const ACTION_LABEL = {
  slot_created: { text: "Slot erstellt", color: "bg-[#00CCC9] text-[#0A0A0A]" },
  slot_archived: { text: "Slot archiviert", color: "bg-slate-500 text-white" },
  assigned: { text: "Zugewiesen", color: "bg-emerald-500 text-white" },
  revoked: { text: "Entzogen", color: "bg-red-500 text-white" },
  password_rotated: { text: "Passwort gedreht", color: "bg-[#FFD100] text-[#0A0A0A]" },
  password_revealed_admin: { text: "PW gelesen (Admin)", color: "bg-slate-800 text-white" },
  password_revealed_participant: { text: "PW gelesen (TN)", color: "bg-slate-600 text-white" },
};

function HistoryView({ history }) {
  return (
    <div className="border border-slate-200 bg-white" data-testid="lic-history-list">
      {history.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-500 italic">
          Noch keine Aktivität.
        </div>
      )}
      <ul className="divide-y divide-slate-100">
        {history.map((h) => {
          const meta = ACTION_LABEL[h.action] || {
            text: h.action,
            color: "bg-slate-300 text-slate-700",
          };
          return (
            <li key={h.audit_id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
              <span className={`text-[10px] font-bold uppercase tracking-overline px-2 py-1 ${meta.color}`}>
                {meta.text}
              </span>
              <div className="text-xs text-slate-700 flex-1 min-w-0">
                <span className="font-mono">{h.slot_label || "—"}</span>
                {h.slot_email && <span className="text-slate-400"> · {h.slot_email}</span>}
                {h.participant_name && (
                  <>
                    <span className="text-slate-400"> · </span>
                    <span className="font-medium">{h.participant_name}</span>
                  </>
                )}
                {h.reason && <span className="text-slate-400"> · {h.reason}</span>}
              </div>
              <div className="text-[10px] font-mono text-slate-400">{(h.created_at || "").replace("T", " ").slice(0, 16)}</div>
              {h.by_user_name && (
                <div className="text-[10px] text-slate-500">von {h.by_user_name}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  Dialogs
// ─────────────────────────────────────────────────────────
function SlotDialog({ initial, templates, onClose, onSaved }) {
  const isEdit = !!initial?.slot_id;
  const [form, setForm] = useState({
    template_id: initial.template_id || (templates[0]?.template_id || ""),
    slot_label: initial.slot_label || "",
    login_email: initial.login_email || "",
    password: "",
    expires_on: initial.expires_on || "",
    monthly_cost: initial.monthly_cost != null ? String(initial.monthly_cost) : "",
    notes: initial.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (saving) return;
    if (!form.template_id || !form.slot_label || !form.login_email) {
      return toast.error("Bitte Pflichtfelder ausfüllen");
    }
    if (!isEdit && !form.password) {
      return toast.error("Passwort ist erforderlich");
    }
    setSaving(true);
    try {
      const payload = {
        template_id: form.template_id,
        slot_label: form.slot_label,
        login_email: form.login_email,
        expires_on: form.expires_on || null,
        monthly_cost: form.monthly_cost ? parseFloat(form.monthly_cost) : null,
        notes: form.notes,
      };
      if (form.password) payload.password = form.password;
      if (isEdit) {
        await api.patch(`/licenses/slots/${initial.slot_id}`, payload);
      } else {
        await api.post("/licenses/slots", payload);
      }
      toast.success(isEdit ? "Slot aktualisiert" : "Slot angelegt");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="slot-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <div className="text-xs uppercase text-slate-500 font-mono">
            {isEdit ? "Slot bearbeiten" : "Neuer Lizenz-Slot"}
          </div>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">
            {isEdit ? initial.slot_label : "Neuen Zugang anlegen"}
          </DialogTitle>
          <DialogDescription className="sr-only">Zugangsdaten für einen Software-Slot bearbeiten.</DialogDescription>
        </DialogHeader>
        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <Field label="Software-Typ *">
            <select
              value={form.template_id}
              onChange={(e) => set("template_id", e.target.value)}
              className="input-tech"
              data-testid="slot-form-template"
            >
              {templates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.icon_emoji} {t.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Slot-Bezeichnung *">
              <input
                type="text"
                value={form.slot_label}
                onChange={(e) => set("slot_label", e.target.value)}
                placeholder="designki01"
                className="input-tech"
                data-testid="slot-form-label"
              />
            </Field>
            <Field label="Ablauf-Datum">
              <GermanDateInput
                
                value={form.expires_on || ""}
                onChange={(e) => set("expires_on", e.target.value)}
                className="input-tech"
                data-testid="slot-form-expires"
              />
            </Field>
          </div>
          <Field label="Login-E-Mail *">
            <input
              type="email"
              value={form.login_email}
              onChange={(e) => set("login_email", e.target.value)}
              placeholder="konto01@beispiel.de"
              className="input-tech"
              data-testid="slot-form-email"
            />
          </Field>
          <Field label={isEdit ? "Passwort (nur ändern falls neu)" : "Passwort *"}>
            <input
              type="text"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder={isEdit ? "leer lassen, um beizubehalten" : "••••••••"}
              className="input-tech font-mono"
              data-testid="slot-form-password"
            />
          </Field>
          <Field label="Monatliche Kosten (€)">
            <input
              type="number"
              step="0.01"
              value={form.monthly_cost}
              onChange={(e) => set("monthly_cost", e.target.value)}
              placeholder="12.00"
              className="input-tech"
              data-testid="slot-form-cost"
            />
          </Field>
          <Field label="Notiz (nur intern)">
            <textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
              className="input-tech resize-none"
              data-testid="slot-form-notes"
            />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 hover:bg-slate-100">
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="slot-form-save"
            className="px-4 py-2 text-sm bg-[#00CCC9] text-[#0A0A0A] font-semibold hover:brightness-105 disabled:opacity-50"
          >
            {saving ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ slot, participants, onClose, onSaved }) {
  const [selected, setSelected] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("all"); // all | wb | avgs | team
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let base = participants;
    if (kindFilter !== "all") base = base.filter((p) => p.kind === kindFilter);
    if (!q) return base;
    return base.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.email || "").toLowerCase().includes(q) ||
        (p.class?.name || "").toLowerCase().includes(q),
    );
  }, [participants, filter, kindFilter]);

  const save = async () => {
    if (!selected) return toast.error("Bitte Teilnehmer/-in auswählen");
    setSaving(true);
    try {
      await api.post(`/licenses/slots/${slot.slot_id}/assign`, {
        participant_id: selected,
        note,
      });
      toast.success("Zugang zugewiesen");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-none border border-slate-200 p-0" data-testid="assign-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <div className="text-xs uppercase text-slate-500 font-mono">
            Zugang zuweisen · {slot.template?.name} Slot {slot.slot_label}
          </div>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">
            Person auswählen
          </DialogTitle>
          <DialogDescription className="sr-only">Wähle einen Teilnehmer, um ihm/ihr diesen Zugang zuzuweisen.</DialogDescription>
        </DialogHeader>
        <div className="px-6 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Nach Name / E-Mail / Klasse suchen…"
            className="input-tech"
            data-testid="assign-filter"
          />
          <div className="flex gap-1 flex-wrap">
            {[
              { k: "all",  l: "Alle" },
              { k: "wb",   l: "Weiterbildung" },
              { k: "avgs", l: "AVGS-Coaching" },
              { k: "team", l: "Team (Coach/Dozent)" },
            ].map((o) => (
              <button key={o.k} onClick={() => setKindFilter(o.k)}
                className={`px-2.5 py-1 text-[11px] border ${kindFilter === o.k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:bg-slate-50"}`}
                data-testid={`assign-kind-${o.k}`}>{o.l}</button>
            ))}
          </div>
          <ul className="divide-y divide-slate-100 border border-slate-100">
            {filtered.length === 0 && (
              <li className="p-4 text-sm text-slate-500 italic">Keine Person gefunden.</li>
            )}
            {filtered.map((p) => (
              <li key={p.participant_id}>
                <label
                  className={`w-full flex items-center gap-3 py-3 px-3 cursor-pointer hover:bg-slate-50 ${
                    selected === p.participant_id ? "bg-[#00CCC9]/10" : ""
                  }`}
                  data-testid={`assign-row-${p.participant_id}`}
                >
                  <input
                    type="radio"
                    name="assignee"
                    checked={selected === p.participant_id}
                    onChange={() => setSelected(p.participant_id)}
                    className="accent-[#00CCC9]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{p.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">
                      {p.email}
                      {p.class && (
                        <>
                          {" · "}
                          {p.class.measure_number} {p.class.name}
                        </>
                      )}
                    </div>
                  </div>
                </label>
              </li>
            ))}
          </ul>
          <Field label="Notiz (optional)">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z.B. bis Kursende der Klasse"
              className="input-tech"
              data-testid="assign-note"
            />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 hover:bg-slate-100">
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving || !selected}
            data-testid="assign-save"
            className="px-4 py-2 text-sm bg-[#00CCC9] text-[#0A0A0A] font-semibold hover:brightness-105 disabled:opacity-50"
          >
            {saving ? "Weist zu…" : "Zuweisen"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplateDialog({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.template_id;
  const [form, setForm] = useState({
    name: initial.name || "",
    icon_emoji: initial.icon_emoji || "🔑",
    color: initial.color || "#00CCC9",
    login_url: initial.login_url || "",
    monthly_cost: initial.monthly_cost != null ? String(initial.monthly_cost) : "",
    notes: initial.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [hasLogo, setHasLogo] = useState(!!initial.has_logo);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(f.type)) {
      return toast.error("Nur PNG, JPG, WEBP oder SVG erlaubt");
    }
    if (f.size > 2 * 1024 * 1024) return toast.error("Logo zu groß (max 2 MB)");
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const removeLogo = async () => {
    if (!isEdit) { setLogoFile(null); setLogoPreview(null); return; }
    try {
      await api.delete(`/licenses/templates/${initial.template_id}/logo`);
      setHasLogo(false);
      setLogoFile(null);
      setLogoPreview(null);
      toast.success("Logo entfernt");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const save = async () => {
    if (!form.name) return toast.error("Name erforderlich");
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        icon_emoji: form.icon_emoji,
        color: form.color,
        login_url: form.login_url || null,
        monthly_cost: form.monthly_cost ? parseFloat(form.monthly_cost) : 0,
        notes: form.notes,
      };
      let templateId = initial.template_id;
      if (isEdit) await api.patch(`/licenses/templates/${templateId}`, payload);
      else {
        const r = await api.post("/licenses/templates", payload);
        templateId = r.data.template_id;
      }
      if (logoFile && templateId) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post(`/licenses/templates/${templateId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Gespeichert");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-none border border-slate-200 p-0" data-testid="template-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100">
          <div className="text-xs uppercase text-slate-500 font-mono">Software-Typ</div>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">
            {isEdit ? "Bearbeiten" : "Neu anlegen"}
          </DialogTitle>
          <DialogDescription className="sr-only">Software-Vorlage für neue Lizenzen anlegen oder ändern.</DialogDescription>
        </DialogHeader>
        <div className="px-6 py-5 space-y-3">
          <Field label="Name *">
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="input-tech"
              placeholder="z.B. Runway ML"
              data-testid="tpl-name"
            />
          </Field>
          <Field label="Logo (Bild statt Emoji, optional)">
            <div className="flex items-center gap-3">
              <div
                className="w-14 h-14 flex items-center justify-center text-3xl shrink-0 overflow-hidden border border-slate-200"
                style={{ background: `${form.color}18`, color: form.color }}
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Vorschau" className="w-full h-full object-contain p-1" />
                ) : hasLogo ? (
                  <img src={`${API}/licenses/templates/${initial.template_id}/logo`} alt="Logo" className="w-full h-full object-contain p-1" />
                ) : (
                  form.icon_emoji || "🔑"
                )}
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <label className="text-xs border border-slate-300 hover:bg-slate-100 px-3 py-1.5 text-center cursor-pointer" data-testid="tpl-logo-upload">
                  Bild hochladen
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onPickLogo} className="hidden" />
                </label>
                {(hasLogo || logoPreview) && (
                  <button onClick={removeLogo} className="text-xs text-red-600 hover:underline" data-testid="tpl-logo-remove">
                    Logo entfernen (Emoji verwenden)
                  </button>
                )}
              </div>
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Emoji-Icon (Fallback)">
              <input
                type="text"
                value={form.icon_emoji}
                onChange={(e) => set("icon_emoji", e.target.value)}
                className="input-tech text-2xl text-center"
                maxLength={4}
                data-testid="tpl-emoji"
              />
            </Field>
            <Field label="Akzent-Farbe">
              <input
                type="color"
                value={form.color}
                onChange={(e) => set("color", e.target.value)}
                className="w-full h-[42px] border border-slate-300 cursor-pointer"
                data-testid="tpl-color"
              />
            </Field>
          </div>
          <Field label="Login-URL">
            <input
              type="url"
              value={form.login_url}
              onChange={(e) => set("login_url", e.target.value)}
              placeholder="https://…"
              className="input-tech"
              data-testid="tpl-url"
            />
          </Field>
          <Field label="Monatliche Kosten pro Slot (€)">
            <input
              type="number"
              step="0.01"
              value={form.monthly_cost}
              onChange={(e) => set("monthly_cost", e.target.value)}
              placeholder="12.00"
              className="input-tech"
              data-testid="tpl-cost"
            />
          </Field>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 hover:bg-slate-100">
            Abbrechen
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="tpl-save"
            className="px-4 py-2 text-sm bg-[#00CCC9] text-[#0A0A0A] font-semibold hover:brightness-105 disabled:opacity-50"
          >
            {saving ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────
//  Small building blocks
// ─────────────────────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, tone = "slate" }) {
  const bg =
    {
      teal: "bg-[#00CCC9]/10 border-[#00CCC9]",
      yellow: "bg-[#FFD100]/10 border-[#FFD100]",
      green: "bg-emerald-50 border-emerald-500",
      slate: "bg-white border-slate-200",
    }[tone] || "bg-white border-slate-200";
  return (
    <div className={`${bg} border-l-2 px-4 py-3`}>
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1">
        {Icon && <Icon size={11} strokeWidth={1.5} />} {label}
      </div>
      <div className="font-heading text-3xl font-light tracking-tighter mt-1">{value}</div>
    </div>
  );
}

function Tab({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
        active ? "border-[#00CCC9] text-[#0A0A0A]" : "border-transparent text-slate-500 hover:text-[#0A0A0A]"
      }`}
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1.5 block">
        {label}
      </label>
      {children}
    </div>
  );
}
